import AppLink from "components/ui/AppLink";
import SortableTableHeader from "components/ui/SortableTableHeader";
import EndpointsDataGrid from "components/ui/EndpointsDataGrid";
import { GridColDef } from "@mui/x-data-grid";
import {
  ComplianceStatusToString,
  decodeUriString,
  getSorter,
  normalizeString,
  statusToNumber,
  toDate,
  toFormatedDate,
} from "system/libraries/utilities";
import { ListResponse, SubjectStatusAtTime } from "system/types/wireTypes";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import useApi from "system/hooks/use-api";
import useMultiFilters from "system/hooks/use-multi-filters";
import usePageNavigation from "system/hooks/use-page-navigation";
import usePageTitle from "system/hooks/use-page-title";
import useSearchField from "system/hooks/use-search-field";
import ComplianceStatus from "components/ui/ComplianceStatus";
import LoadingPage from "./system/LoadingPage";
import useBreadcrumbs from "system/hooks/use-breadcrumbs";
import IAppBreadcrumb from "system/types/interfaces/IAppBreadcrumb";
import ITitleData from "system/types/interfaces/ITitleData";
import ActionButtons from "components/ui/ActionButtons";
import ExportToCsvButton from "components/ui/ExportToCsvButton";
import EndpointFilter from "components/ui/EndpointFilter";
import RetryErrorPage from "./system/RetryErrorPage";

const EndpointsByReasonPage = () => {
  const api = useApi();
  const { statusString, subject, time, message } = useParams();
  const { sortDesc, sortField } = useSearchField();
  const { navigateToEndpointPage, getSubjectByReasonPageUrl , getHomePageUrl } = usePageNavigation();
  const [data, setData] = useState<SubjectStatusAtTime[] | null>(null);
  const { machineFilterSet } = useMultiFilters();
  const [error, setError] = useState<string | null>(null);

  const decodedMessage = useMemo(() => decodeUriString(message), [message]);

  const refreshHandler = () => {
    setData(null);
    setError(null);
    loadMachinesByReasonData();
  };

  usePageTitle(
    useMemo<ITitleData>(() => {
      let result: ITitleData = {
        title: `${subject ? `${subject} ` : ""}${subject && decodedMessage ? "| " : ""}${decodedMessage ? ` ${decodedMessage}` : ""}`,
        subTitle: decodedMessage ? "Status Message" : "Endpoints by state of subject",
        subject: subject,
      };

      if (statusString && statusString !== "all") {
        result.title += ` | ${normalizeString(statusString)}`;
      }

      return result;
    }, [subject, statusString, decodedMessage])
  );

  useBreadcrumbs(
    useMemo<IAppBreadcrumb[]>(() => {
      let result: IAppBreadcrumb[] = [];

      if (time) {
        result.push({
          title: "Time",
          name: toFormatedDate(time),
          url: getHomePageUrl(time),
        });
      }

      if (subject) {
        result.push({
          title: "Subject",
          name: subject,
          url: getSubjectByReasonPageUrl(subject, time),
        });
      }

      if (statusString && statusString !== "all") {
        result.push({
          title: "State",
          name: normalizeString(statusString),
        });
      }

      if (decodedMessage) {
        result.push({
          title: "Message",
          name: decodedMessage,
        });
      }

      return result;
    }, [time, subject, statusString, decodedMessage, getSubjectByReasonPageUrl])
  );

  const getMachinesByReasonDataForExport = useCallback(() => {
    const complianceStatus = statusToNumber(statusString ?? null);
    if ((complianceStatus === null && statusString !== "all") || !subject || !time) {
      return;
    }

    return api.list.machinesByReason({
      complianceStatus: complianceStatus ?? 0,
      filters: machineFilterSet,
      subject,
      fromTime: new Date(time),
      packageMessage: decodedMessage ?? null,
      isUnknown: complianceStatus === 0,
      noLimit: true,
    });
  }, [api, statusString, subject, time, decodedMessage, machineFilterSet]);

  const exportDataHandler = useCallback(async () => {
    const csvDataToExport = await getMachinesByReasonDataForExport()
      ?.then((data: ListResponse<SubjectStatusAtTime> | null) => {
        return data?.isSuccess ? data.items : null;
      })
      .catch(() => null);

    return csvDataToExport?.map((x) => ({
      Endpoint: x.computerName,
      Package: x.packageName,
      State: ComplianceStatusToString(x.complianceStatus),
      Justification: x.complianceJustification,
      AssessmentTime: toFormatedDate(x.referenceJsTime!),
      AssessmentTimeISO8601: x.referenceJsTime,
      LastRecorded: toFormatedDate(x.lastReportedJsTime!),
      LastRecordedISO8601: x.lastReportedJsTime,
      UseCase: x.packageType,
      Rule: x.ruleDescription,
      LastExitCode: x.packageExitCode,
      StatusMessage: x.applicabilityMessage,
    }));
  }, [getMachinesByReasonDataForExport]);

  const loadMachinesByReasonData = useCallback(() => {
    const complianceStatus = statusToNumber(statusString ?? null);
    if ((complianceStatus === null && statusString !== "all") || !subject || !time) {
      return;
    }

    const controller = new AbortController();
    const fetchData = async () => {
      try {
        const data = await api.list.machinesByReason(
          {
            complianceStatus: complianceStatus ?? 0,
            filters: machineFilterSet,
            subject,
            fromTime: new Date(time),
            packageMessage: decodedMessage ?? null,
            isUnknown: complianceStatus === 0,
            noLimit: true,
          },
          {
            retries: 3,
            baseDelay: 1000,
            maxDelay: 10000,
            signal: controller.signal,
          }
        );

        if (data && data?.isSuccess) {
          setData(data.items);
          setError(null);
        } else {
          setError(data?.failMessage || "Failed to load endpoint data after multiple attempts.");
        }
      } catch (error: any) {
        if (error.name === "AbortError") return;
        console.error("Error fetching endpoint data:", error);
        setError(error.message || "Failed to load endpoint data after multiple attempts.");
      }
    };

    fetchData();

    return () => {
      controller.abort();
      setData(null);
      setError(null);
    };
  }, [setData, api, statusString, subject, time, decodedMessage, machineFilterSet]);

  useEffect(() => loadMachinesByReasonData(), [loadMachinesByReasonData]);

  const sortedData = useMemo<typeof data>(() => {
    if (data === null) {
      return null;
    }

    switch (sortField) {
      case "endpoint":
        return [...data].sort(getSorter("computerName", sortDesc));
      case "package":
        return [...data].sort(getSorter("packageName", sortDesc));
      case "state":
        return [...data].sort(getSorter("isUnknown", sortDesc)).sort(getSorter("complianceStatus", sortDesc));
      case "justification":
        return [...data].sort(getSorter("complianceJustification", sortDesc));
      case "time":
        return [...data].sort(getSorter("referenceJsTime", sortDesc));
      case "lastrecorded":
        return [...data].sort(getSorter("lastReportedJsTime", sortDesc));
      case "usecase":
        return [...data].sort(getSorter("packageType", sortDesc));
      case "rule":
        return [...data].sort(getSorter("ruleDescription", sortDesc));
      case "exitcode":
        return [...data].sort(getSorter("packageExitCode", sortDesc));
      case "message":
        return [...data].sort(getSorter("applicabilityMessage", sortDesc));
      default:
        return [...data];
    }
  }, [data, sortField, sortDesc]);

  const columns = useMemo<GridColDef[]>(() => {
    // Headers stay the original component so sorting keeps writing sort/desc to the url.
    const header = (title: string, property: string) => () => <SortableTableHeader title={title} property={property} />;
    const base = { sortable: false, disableColumnMenu: true, resizable: false };

    return [
      {
        ...base,
        field: "endpoint",
        width: 159,
        renderHeader: header("Endpoint", "endpoint"),
        renderCell: (params) => (
          <AppLink onClick={() => navigateToEndpointPage(params.row.machineId, time)}>{params.row.computerName}</AppLink>
        ),
      },
      { ...base, field: "package", width: 117, renderHeader: header("Package", "package"), valueGetter: (_v, row) => row.packageName },
      {
        ...base,
        field: "state",
        // Measured 106 in prod, but that table only showed "Not needed". The html
        // table grows with its content and the grid cannot, so this fits the
        // longest badge: "Action recommended (not reporting)".
        minWidth: 106,
        width: 255,
        renderHeader: header("State", "state"),
        renderCell: (params) => <ComplianceStatus code={params.row.complianceStatus} isUnknown={params.row.isUnknown} isSubject={true} />,
      },
      {
        ...base,
        field: "justification",
        width: 266,
        // Without a floor this column collapses to nothing on a narrow window and
        // the text wraps one word per line.
        minWidth: 240,
        flex: 1,
        renderHeader: header("Justification", "justification"),
        valueGetter: (_v, row) => row.complianceJustification,
      },
      {
        ...base,
        field: "time",
        width: 178,
        renderHeader: header("Assessment changed", "time"),
        valueGetter: (_v, row) => toFormatedDate(row.referenceJsTime),
      },
      {
        ...base,
        field: "lastrecorded",
        width: 146,
        renderHeader: header("Last recorded", "lastrecorded"),
        valueGetter: (_v, row) => toFormatedDate(row.lastReportedJsTime),
      },
      { ...base, field: "usecase", width: 100, renderHeader: header("Use case", "usecase"), valueGetter: (_v, row) => row.packageType },
      { ...base, field: "rule", width: 116, renderHeader: header("Rule", "rule"), valueGetter: (_v, row) => row.ruleDescription },
      {
        ...base,
        field: "exitcode",
        width: 132,
        renderHeader: header("Last exit code", "exitcode"),
        valueGetter: (_v, row) => row.packageExitCode,
      },
      {
        ...base,
        field: "message",
        width: 148,
        renderHeader: header("Status message", "message"),
        valueGetter: (_v, row) => row.applicabilityMessage,
      },
    ];
  }, [navigateToEndpointPage, time]);

  const exportData = useMemo(
    () => ({
      fileName: `Aiden ${subject ? `${subject} ` : ""}${decodedMessage ? `${decodedMessage} ` : ""}${
        statusString && statusString !== "all" ? `${normalizeString(statusString)} ` : ""
      }${decodedMessage ? "Status Message " : "Endpoints by state of subject "}${toDate(time)}`,
      source: exportDataHandler,
    }),
    [exportDataHandler, decodedMessage, subject, time, statusString]
  );

  if (error) {
    return <RetryErrorPage errorMessage="We could not retrieve the information for this page." refreshHandler={refreshHandler} />;
  }

  return sortedData != null ? (
    <>
      <ActionButtons onRefresh={refreshHandler}>{(decodedMessage ?? subject) && <ExportToCsvButton items={[exportData]} />}</ActionButtons>
      <EndpointFilter />
      <EndpointsDataGrid rows={sortedData} columns={columns} />
    </>
  ) : (
    <LoadingPage />
  );
};

export default EndpointsByReasonPage;
